import locale from "antd/locale/nl_NL";
import "dayjs/locale/nl";
import React, { useEffect, useState } from "react";
import { Card, ConfigProvider, Tag, Descriptions, Alert } from "antd";
import {
  createForm,
  setValidateLanguage,
  registerValidateLocale,
  onFieldValueChange,
} from "@formily/core";
import { FormProvider, ISchema, createSchemaField } from "@formily/react";
import { scrollToElement } from "@rumble-it/common-lib/src/helpers/scrollTo";
import {
  RumbleWelcome,
  RumbleGridStep,
  RumbleBoxSelect,
  RumbleDamageSideSelector,
  RumbleCompaniesSelect,
  RumbleServicePartnerSelector,
  RumbleDealershipSelector,
  RumbleLocationSelector,
  RumbleStepButtons,
  RumbleImageUpload,
  RumbleMerkzeker,
  RumbleLabel,
  RumbleFormItem,
  RumbleFormGrid,
  RumbleStepNumberTag,
  RumbleCalendar,
  RumbleTimeSlotPicker,
  LicensePlate,
  Address,
  NumberPicker,
  Email,
  Telephone,
  Input,
  TextArea,
  Text,
  Html,
  Select,
  Switch,
  FormItem,
  Password,
  DatePicker,
  Button,
  Grid,
  Flex,
  Debug,
  RumbleSignature,
  RumbleImageCapturePending,
  RumbleQrCode,
  RumbleImageCaptureHtml,
  RumbleWelcomeStepRow,
  Image,
} from "@rumble-it/common-components/components/Rumble";

import { resolveFormConfigurationTemplate } from "@rumble-it/common-lib/src/helpers/formConfiguration";
import { fetchData } from "../../lib/api/fetchData";
import {
  getServicePartner,
  getServicePartnerCollection,
} from "../../lib/queries/getServicePartner";
import { uploadMedia } from "../../lib/api/uploadMedia";
import { track } from "@rumble-it/common-lib/src/helpers/tracker";
import { autorun } from "@formily/reactive";
import { Upload, Form, FormStep, FormGrid, Checkbox } from "@formily/antd-v5";
import {
  DashboardOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { createStore, StateMachineProvider } from "little-state-machine";
import { getFormConfiguration } from "../../lib/queries/getFormConfiguration";
import { getProperty, setProperty, deleteProperty } from "dot-prop";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LoadingState } from "./loading-state";
import { getFormConfigurationTheme } from "../../lib/queries/getFormConfigurationTheme";
import { getAppointmentTimeSlotsByServicePartner } from "../../lib/queries/getAppointmentTimeSlotsByServicePartner";
import { validatePhoneNumber } from "@rumble-it/common-lib/helpers/phoneNumber";
import { getCauseOfDamages } from "../../lib/queries/getCauseOfDamages";
import { getInsuranceTypes } from "../../lib/queries/getInsuranceTypes";
import { getDamageFromDirections } from "src/lib/queries/getDamageFromDirections";
import { getDriverTypes } from "src/lib/queries/getDriverTypes";
import { getPaymentByTypes } from "src/lib/queries/getPaymentByTypes";
import { getReplacementVehicleTypes } from "src/lib/queries/getReplacementVehicleTypes";
import { getTypeOfDamages } from "src/lib/queries/getTypeOfDamages";
import { format } from "date-fns";
import hash from "hash-it";
import dayjs from "dayjs";

const AntdIcons = {
  DashboardOutlined,
  MailOutlined,
  PhoneOutlined,
};

let formSuccess;

const globalFieldSchema = {
  global: {
    type: "void",
    "x-visible": false,
    "x-data": {
      buttonState: {
        disabled: {
          back: false,
          next: false,
          submit: false,
        },
      },
    },
  },
};

const injectGlobalSchemaFields = (schema: ISchema) => {
  if (!schema?.properties) return schema; // fallback for broken schemas
  if (schema.properties.global) return schema; // avoid injecting multiple times when 'global' is already present

  return {
    ...schema,
    properties: {
      global: globalFieldSchema.global,
      ...(schema.properties as Record<string, ISchema>),
    },
  };
};

const form = createForm({
  effects: () => {
    onFieldValueChange("*", (field) => {
      if (field.value === "") {
        /* @ts-ignore */
        field.setValue(null);
      }
    });
  },
});

const formStep = FormStep.createFormStep();

const SchemaField = createSchemaField({
  components: {
    FormGrid,
    FormStep,
    FormItem,
    Email,
    Telephone,
    Input,
    RumbleInput: Input,
    NumberPicker,
    Password,
    Select,
    RumbleSelect: Select,
    DatePicker,
    RumbleLicensePlate: LicensePlate,
    LicensePlate,
    RumbleWelcome,
    RumbleGridStep,
    RumbleDamageSideSelector,
    RumbleDatePicker: DatePicker,
    RumbleCompaniesSelect,
    RumbleServicePartnerSelector,
    RumbleDealershipSelector,
    RumbleLocationSelector,
    RumbleMerkzeker,
    RumbleLabel,
    RumbleFormItem,
    RumbleFormGrid,
    RumbleStepNumberTag,
    Card,
    Html,
    RumbleHtml: Html,
    Upload,
    RumbleTextArea: TextArea,
    TextArea,
    RumbleText: Text,
    Text,
    Button,
    RumbleStepButtons,
    Address,
    RumbleAddress: Address,
    RumbleImageUpload,
    Tag,
    Descriptions,
    RumbleSwitch: Switch,
    RumbleBoxSelect,
    Switch,
    Grid,
    RumbleGrid: Grid,
    Flex,
    RumbleFlex: Flex,
    Debug,
    RumbleSignature,
    RumbleImageCapturePending,
    RumbleCalendar,
    RumbleTimeSlotPicker,
    RumbleQrCode,
    RumbleImageCaptureHtml,
    RumbleWelcomeStepRow,
    Image,
    Checkbox,
  },
});

setValidateLanguage("nl-NL");

registerValidateLocale({
  "nl-NL": {
    required: "Dit veld is verplicht",
    enum: "Dit veld moet een van de volgende waarden zijn: {values}",
    pattern: "Dit veld moet voldoen aan het patroon",
    email: "Dit veld moet een geldig e-mailadres zijn",
  },
});

interface FormContainerProps {
  formId: string;
  themeId?: string;
  servicePartnerId?: string;
  servicePartnerCollectionId?: string;
}

export const FormContainer = (props: FormContainerProps) => {
  const [formFinish, setFinish] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const STORAGE_KEY = `rumbledirect-form-${hash({
    formId: props.formId,
    themeId: props.themeId,
    servicePartnerId: props.servicePartnerId,
    servicePartnerCollectionId: props.servicePartnerCollectionId,
  })}`;

  const queryClient = useQueryClient();

  const schemaQuery = useQuery({
    queryKey: ["formConfiguration", props.formId],
    queryFn: () => getFormConfiguration(props.formId),
  });

  const formConfiguration = schemaQuery.data?.formConfiguration;
  const etag = schemaQuery.data?.etag;

  const themeQuery = useQuery({
    queryKey: ["formConfigurationTheme", props.themeId],
    queryFn: () => {
      if (!props.themeId && !formConfiguration?.settings?.themeId) {
        throw new Error(
          "Theme ID is required to fetch form configuration theme.",
        );
      }
      const themeId = props.themeId ?? formConfiguration?.settings?.themeId;
      if (typeof themeId !== "string") {
        throw new Error("Theme ID must be a string.");
      }
      return getFormConfigurationTheme(themeId);
    },
    enabled: !!props.themeId || !!formConfiguration?.settings?.themeId,
  });

  /**
   * Check if formConfiguration is present and if not, use legacy theme
   */
  const formConfigurationFallback = formConfiguration?.settings?.theme;
  const formConfigurationTheme =
    !!props.themeId || !!formConfiguration?.settings?.themeId
      ? themeQuery.data?.formConfigurationTheme
      : formConfigurationFallback;

  const servicePartnerQuery = useQuery({
    queryKey: ["servicePartner", props.servicePartnerId],
    queryFn: async () => {
      if (!props.servicePartnerId) {
        throw new Error(
          "Service partner ID is required to fetch service partner data.",
        );
      }
      return getServicePartner(props.servicePartnerId);
    },
    enabled: !!props.servicePartnerId,
  });
  const servicePartner = servicePartnerQuery.data;

  useEffect(() => {
    if (servicePartner) {
      form.setValues({
        servicePartner: {
          servicePartnerIri: servicePartner["iri"],
          ...servicePartner,
        },
      });
    }
  }, [servicePartner]);

  const servicePartnerCollectionQuery = useQuery({
    queryKey: ["servicePartnerCollection", props.servicePartnerCollectionId],
    queryFn: async () => {
      if (!props.servicePartnerCollectionId) {
        throw new Error(
          "Service partner collection ID is required to fetch service partner data.",
        );
      }
      return getServicePartnerCollection(props.servicePartnerCollectionId);
    },
    enabled: !!props.servicePartnerCollectionId,
  });
  const servicePartnerCollection = servicePartnerCollectionQuery.data;

  useEffect(() => {
    if (servicePartnerCollection) {
      form.setValues({
        servicePartnerCollection,
      });
    }
  }, [servicePartnerCollection]);

  /**
   * Check if servicePartnerId is present in the props
   */
  if (
    props.servicePartnerId &&
    (servicePartnerQuery.isPending || servicePartnerQuery.isError)
  ) {
    return (
      <LoadingState
        isPending={servicePartnerQuery.isPending}
        isError={servicePartnerQuery.isError}
        errorMessage="De informatie kon niet worden geladen. Probeer het later opnieuw of neem contact op met de klantenservice."
      />
    );
  }

  /**
   * Check if servicePartnerCollectionId is present in the props
   */
  if (
    props.servicePartnerCollectionId &&
    (servicePartnerCollectionQuery.isPending ||
      servicePartnerCollectionQuery.isError)
  ) {
    return (
      <LoadingState
        isPending={servicePartnerCollectionQuery.isPending}
        isError={servicePartnerCollectionQuery.isError}
        errorMessage="De informatie kon niet worden geladen. Probeer het later opnieuw of neem contact op met de klantenservice."
      />
    );
  }

  /**
   * Check if formConfiguration is present
   */
  if (schemaQuery.isPending || schemaQuery.isError) {
    return (
      <LoadingState
        isPending={schemaQuery.isPending}
        isError={schemaQuery.isError}
        errorMessage="Het formulier kon niet worden geladen. Probeer het later opnieuw of neem contact op met de klantenservice."
      />
    );
  }

  /**
   * Check if formConfiguration theme is present
   */
  if (
    !formConfigurationFallback &&
    (themeQuery.isPending || themeQuery.isError)
  ) {
    return (
      <LoadingState
        isPending={themeQuery.isPending}
        isError={themeQuery.isError}
        errorMessage="Het thema kon niet worden geladen. Probeer het later opnieuw of neem contact op met de klantenservice."
      />
    );
  }

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed: {
        etag?: string | null;
        values?: Record<string, any>;
        currentStep?: number | null;
      } | null = JSON.parse(stored);
      // if the etag is equal as the etag we have stored, we can use this data
      if (parsed !== null && etag === parsed.etag) {
        form.setInitialValues(parsed.values ?? {});

        // Make sure step index is in valid range
        if (typeof parsed.currentStep === "number" && parsed.currentStep >= 0) {
          formStep.setCurrent(parsed.currentStep);
        }
      }
    } catch (error) {
      console.warn("Failed to load saved form state:", error);
    }
  }

  /**
   * Add tracking to the form steps
   */
  track({
    type: "init",
    data: {
      name: formConfiguration.settings.name,
    },
  });

  autorun(() => {
    /**
     * Track steps
     */
    track({
      type: "step",
      data: {
        step: formStep.current + 1,
      },
    });

    /**
     * Save data to local storage - only if form is active (not in success state)
     */
    if (!formFinish) {
      const data = {
        values: form.values,
        currentStep: formStep.current,
        etag: etag,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
  });

  const formSubmit = async () => {
    // Clear any previous errors
    setSubmitErrors([]);
    setShowErrors(false);

    for (const [_path, field] of Object.entries(form.fields)) {
      if (field.componentType === "RumbleSignature") {
        try {
          const blob = await fetch(field.value as string).then((res) =>
            res.blob(),
          );
          const file = new File(
            [blob],
            `${field.props.filename ?? field.props.name}.png`,
            {
              type: "image/png",
            },
          );

          const response = await uploadMedia({
            file,
            onSuccess: () => {},
            onError: () => {},
          });

          if (!response || !response["@id"]) {
            console.error(
              "form-container.tsx: Failed to upload signature:",
              response,
            );
          }

          form.setValuesIn(field.props.name, response["@id"]);
        } catch (err) {
          console.error("form-container.tsx: Failed to upload signature", err);
        }
      }
    }

    const formattedData = resolveFormConfigurationTemplate(
      formConfiguration.settings.format,
      form,
    );

    // Add source field
    formattedData.source = "widget";

    try {
      /**
       * Post data to the endpoint
       */
      const response = await fetchData({
        endpoint: formConfiguration.settings.endpoint,
        method: "POST",
        data: formattedData,
      });

      const dossier = response.data.dossier ?? null;
      const imageCapture = response.data.imageCapture ?? null;
      const servicePartner = response.data.servicePartner ?? null;
      const dealershipCompany = response.data.dealershipCompany ?? null;

      /**
       * Track finish
       */
      track({
        type: "finish",
        data: {
          dossierId: dossier?.id ?? response.data.id,
          name: formConfiguration.settings.name,
        },
      });

      /**
       * Use form values to set the success form
       */
      const successValues = form.values;

      /**
       * Check if imageCaptureUrl is present in the dossier
       */
      if (imageCapture?.url) {
        successValues.showImageCapture = true;
      }

      /**
       * Create the success form
       */
      formSuccess = createForm({
        values: {
          ...successValues,
          dossier: dossier,
          imageCapture: imageCapture,
          servicePartner: servicePartner,
          dealershipCompany: dealershipCompany,
        },
      });

      localStorage.removeItem(STORAGE_KEY);

      /**
       * Set the success form configuration
       */
      setFinish(true);

      scrollToElement({ element: "#rumbledirect-form" });
    } catch (error) {
      console.error("Failed to post form configuration:", error);

      // Handle submission errors
      const errors = [];

      // Check if the error response contains structured error data
      if (error.response?.data?.errors) {
        // API returned structured errors
        errors.push(
          ...error.response.data.errors.map((err) =>
            typeof err === "string" ? err : err.message || String(err),
          ),
        );
      } else if (error.response?.data?.message) {
        // API returned a single error message
        errors.push(error.response.data.message);
      } else if (error.message) {
        // Use the error message directly
        errors.push(error.message);
      } else {
        // Fallback for unknown error formats
        errors.push(
          "Er is een onbekende fout opgetreden bij het versturen van het formulier.",
        );
      }

      // Update the error state to display to the user
      setSubmitErrors(errors);
      setShowErrors(true);
    }
  };

  /**
   * Propagate Little State Machine to the form
   */
  const scope = {
    dayjs,
    getAppointmentTimeSlotsByServicePartner,
    getCauseOfDamages,
    getDamageFromDirections,
    getDriverTypes,
    getInsuranceTypes,
    getPaymentByTypes,
    getReplacementVehicleTypes,
    getTypeOfDamages,
    fetchData: fetchData,
    formStep: formStep,
    formSubmit: formSubmit,
    copyPath: (sourcePath: string, targetPath: string) => {
      const sourceSchema = getProperty(
        formConfiguration.form,
        sourcePath,
        null,
      );
      if (!sourceSchema) {
        console.warn("Path not found in form configuration:", sourcePath);
        return;
      }

      const newFormConfiguration = structuredClone(formConfiguration);

      setProperty(newFormConfiguration.form, targetPath, sourceSchema);

      queryClient.setQueryData(["formConfiguration", props.formId], {
        formConfiguration: newFormConfiguration,
        etag: etag,
      });
    },
    removePath: (targetPath: string) => {
      const newFormConfiguration = structuredClone(formConfiguration);

      deleteProperty(newFormConfiguration.form, targetPath);

      queryClient.setQueryData(["formConfiguration", props.formId], {
        formConfiguration: newFormConfiguration,
        etag: etag,
      });
    },
    uploadMedia: uploadMedia,
    icon: (iconName) => {
      // Dynamically get the icon component from @ant-design/icons
      const IconComponent = AntdIcons[iconName];
      if (IconComponent) {
        // If the icon exists, create and return the React element
        return React.createElement(IconComponent);
      }
      // Optionally, return null or a default icon if not found
      return null;
    },
    validatePhone: (phoneNumber: string) => {
      return validatePhoneNumber({ countryCode: "NL", phoneNumber });
    },
    formatDate: format,
  };
  createStore({ scope: scope });

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "40px 0",
      }}
    >
      <StateMachineProvider>
        <ConfigProvider theme={formConfigurationTheme} locale={locale}>
          <div
            id="rumbledirect-form"
            style={{ width: "100%", maxWidth: "800px" }}
          >
            {/* TODO: Fix this! List classnames for postcss */}
            <div
              ref={(el) => {
                if (el) {
                  el.style.setProperty("display", "none", "important");
                }
              }}
              className="list-disc text-gray-800 ml-6 pt-8 mb-8 mt-0 col-span-2 w-48 border-none hidden sm:block"
            />
            {formFinish ? (
              <FormProvider form={formSuccess}>
                <Form layout={"vertical"}>
                  <SchemaField schema={formConfiguration.onSuccess} />
                </Form>
              </FormProvider>
            ) : (
              <FormProvider form={form}>
                <Form layout={"vertical"}>
                  <SchemaField
                    schema={injectGlobalSchemaFields(formConfiguration.form)}
                    scope={scope}
                  />
                </Form>

                {showErrors && submitErrors.length > 0 ? (
                  <Alert
                    message="Er zijn fouten opgetreden bij het versturen van het formulier"
                    description={
                      <ul style={{ marginBottom: 0, paddingLeft: "20px" }}>
                        {submitErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    }
                    type="error"
                    style={{ marginTop: "24px" }}
                    onClose={() => setShowErrors(false)}
                  />
                ) : null}
              </FormProvider>
            )}
          </div>
        </ConfigProvider>
      </StateMachineProvider>
    </div>
  );
};
